/**
 * Isca "Fazer cadastro com a IA" + chat de cadastro.
 * Renderizado em portal no <body>, então nenhum transform/overflow do layout
 * quebra o `position: fixed` da isca. Não conhece o OQ: recebe a API e os
 * callbacks por props (ver AiSignup.tsx).
 */
import {
  CSSProperties,
  FormEvent,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { maskPhoneBR } from '../../../utils/mask';
import {
  AskEvent,
  DocKey,
  FlowEvent,
  SignupApi,
  SignupFlow,
  SignupPrefill,
  TextPart,
  UI,
  colorTokens,
  createSignupFlow,
} from '../signupFlow';
import '../ai-signup-widget.css';

const SEEN_KEY = 'asw:fab-seen';
const getSeen = () => {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1';
  } catch (_) {
    return false;
  }
};
const setSeen = () => {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch (_) {
    /* navegação privada */
  }
};

function Spark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <g fill="currentColor">
        <path
          className="asw-s1"
          d="M10 3C10.6 8.2 12.8 10.4 18 11C12.8 11.6 10.6 13.8 10 19C9.4 13.8 7.2 11.6 2 11C7.2 10.4 9.4 8.2 10 3Z"
        />
        <path
          className="asw-s2"
          d="M19 1.5C19.25 3.6 19.9 4.25 22 4.5C19.9 4.75 19.25 5.4 19 7.5C18.75 5.4 18.1 4.75 16 4.5C18.1 4.25 18.75 3.6 19 1.5Z"
        />
        <path
          className="asw-s3"
          d="M19 15.5C19.25 17.6 19.9 18.25 22 18.5C19.9 18.75 19.25 19.4 19 21.5C18.75 19.4 18.1 18.75 16 18.5C18.1 18.25 18.75 17.6 19 15.5Z"
        />
      </g>
    </svg>
  );
}

type Message =
  | { id: number; who: 'bot'; parts: TextPart[] }
  | { id: number; who: 'user'; text: string };

interface ChatState {
  msgs: Message[];
  typing: boolean;
  busy: boolean;
  ask: AskEvent | null;
  consent: boolean;
  done: boolean;
}

const initial: ChatState = {
  msgs: [],
  typing: false,
  busy: false,
  ask: null,
  consent: false,
  done: false,
};

function reducer(s: ChatState, a: FlowEvent): ChatState {
  switch (a.type) {
    case 'reset':
      return { ...initial };
    case 'busy':
      return { ...s, busy: a.busy };
    case 'typing':
      return { ...s, typing: a.on };
    case 'bot':
      return {
        ...s,
        msgs: [...s.msgs, { id: s.msgs.length, who: 'bot', parts: a.parts }],
      };
    case 'user':
      return {
        ...s,
        msgs: [...s.msgs, { id: s.msgs.length, who: 'user', text: a.text }],
      };
    case 'ask':
      return { ...s, ask: a };
    case 'consent':
      return { ...s, consent: a.show };
    case 'done':
      return { ...s, done: true, ask: null, consent: false };
    default:
      return s;
  }
}

function Parts({
  parts,
  privacyUrl,
  termsUrl,
  onOpenDoc,
}: {
  parts: TextPart[];
  privacyUrl?: string;
  termsUrl?: string;
  onOpenDoc?: (doc: DocKey) => boolean | void;
}) {
  return (
    <>
      {parts.map((p, i) =>
        p.type === 'text' ? (
          <span key={i}>{p.value}</span>
        ) : (
          <a
            key={i}
            className="asw-link"
            href={(p.doc === 'priv' ? privacyUrl : termsUrl) || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(ev) => {
              // onOpenDoc devolve true quando abriu o documento por conta própria.
              if (onOpenDoc && onOpenDoc(p.doc)) ev.preventDefault();
            }}
          >
            {p.label}
          </a>
        )
      )}
    </>
  );
}

export interface AiSignupWidgetProps {
  api: SignupApi;
  brandName?: string;
  primaryColor?: string;
  privacyUrl?: string;
  termsUrl?: string;
  showAfterMs?: number;
  offsetX?: number;
  offsetY?: number;
  enabled?: boolean;
  onSuccess?: () => void;
  onLogin?: () => void;
  /** Abre um documento dentro do chat. Retorne true para não seguir o link. */
  onOpenDoc?: (doc: DocKey) => boolean | void;
  /** Conteúdo exibido por cima da conversa (ex.: os Termos). */
  overlay?: ReactNode;
  /** Pede para fechar o overlay (botão voltar / Esc). */
  onCloseOverlay?: () => void;
  /** Dados já conhecidos; o chat não pergunta de novo os que forem válidos. */
  prefill?: SignupPrefill;
  /** Abre o chat sempre que este número muda (e é maior que zero). */
  openRequest?: number;
  /** Esconde a isca até a conversa começar (quem abre é a página). */
  launcherOnlyAfterStart?: boolean;
}

export function AiSignupWidget({
  api,
  brandName = '',
  primaryColor,
  privacyUrl,
  termsUrl,
  showAfterMs = 2000,
  offsetX = 16,
  offsetY = 16,
  enabled = true,
  onSuccess,
  onLogin,
  onOpenDoc,
  overlay,
  onCloseOverlay,
  prefill,
  openRequest = 0,
  launcherOnlyAfterStart = false,
}: AiSignupWidgetProps) {
  const [s, dispatch] = useReducer(reducer, initial);
  const [open, setOpen] = useState(false);
  const [fabState, setFabState] = useState<'pre' | 'in'>(() =>
    getSeen() ? 'in' : 'pre'
  );
  const [attn, setAttn] = useState(false);
  const [tease, setTease] = useState(false);
  const [value, setValue] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [agree, setAgree] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const msgsRef = useRef<HTMLDivElement>(null);
  const propsRef = useRef({ api, onSuccess, onLogin, prefill });
  propsRef.current = { api, onSuccess, onLogin, prefill };

  const flowRef = useRef<SignupFlow | null>(null);
  if (!flowRef.current) {
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    flowRef.current = createSignupFlow({
      api: () => propsRef.current.api,
      config: () => ({
        reduceMotion,
        onSuccess: propsRef.current.onSuccess,
        onLogin: propsRef.current.onLogin,
      }),
      emit: (e) => {
        dispatch(e);
        if (e.type === 'goLogin') {
          setOpen(false);
          flowRef.current?.reset();
        }
      },
    });
  }
  const flow = flowRef.current;

  const openChat = () => {
    setOpen(true);
    setTease(false);
    setAttn(false);
    setSeen();
    flow.start(propsRef.current.prefill);
  };

  /* Abertura pedida pela página (ex.: botão de um modal) */
  useEffect(() => {
    if (enabled && openRequest > 0) openChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);

  /* Entrada da isca: alguns segundos depois de montar, da direita para a esquerda */
  useEffect(() => {
    if (!enabled || launcherOnlyAfterStart || fabState !== 'pre')
      return undefined;
    const t = setTimeout(() => {
      setFabState('in');
      setAttn(true);
      setSeen();
    }, showAfterMs);
    return () => clearTimeout(t);
  }, [enabled, launcherOnlyAfterStart, fabState, showAfterMs]);

  /* Balão "Sem formulário: é só conversar" depois da entrada */
  useEffect(() => {
    if (!enabled || launcherOnlyAfterStart || !attn || open) return undefined;
    const t1 = setTimeout(() => setTease(true), 1400);
    const t2 = setTimeout(() => setTease(false), 10400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [enabled, launcherOnlyAfterStart, attn, open]);

  /* Isca sempre visível no celular: acompanha o teclado e as barras do navegador */
  useEffect(() => {
    const el = rootRef.current;
    if (!enabled || !el) return undefined;
    const vv = window.visualViewport;
    const update = () => {
      const kb = vv
        ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
        : 0;
      el.style.setProperty('--asw-kb', kb + 'px');
      el.style.setProperty(
        '--asw-vh',
        (vv ? vv.height : window.innerHeight) + 'px'
      );
    };
    update();
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }
    window.addEventListener('resize', update);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
      window.removeEventListener('resize', update);
    };
  }, [enabled]);

  /* Rolagem automática, foco e Esc */
  useEffect(() => {
    const m = msgsRef.current;
    if (m) m.scrollTop = m.scrollHeight;
  }, [s.msgs, s.typing, s.consent, open]);
  useEffect(() => {
    setValue('');
    setShowPwd(false);
  }, [s.ask]);
  useEffect(() => {
    if (open && !overlay && s.ask && s.ask.field && inputRef.current)
      inputRef.current.focus({ preventScroll: true });
  }, [s.ask, open, overlay]);
  useEffect(() => {
    if (s.consent) setAgree(false);
  }, [s.consent]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      if (overlay) onCloseOverlay?.();
      else setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, overlay, onCloseOverlay]);

  const tokens = useMemo(() => colorTokens(primaryColor), [primaryColor]);

  if (!enabled || typeof document === 'undefined') return null;

  const field = s.ask && s.ask.field;
  const started = s.msgs.length > 0;
  const progress = s.done
    ? 100
    : s.ask && s.ask.index >= 0
      ? (s.ask.index / s.ask.total) * 100 + 4
      : 0;
  const subtitle = s.done
    ? UI.done
    : s.ask && s.ask.index >= 0
      ? `Passo ${s.ask.index + 1} de ${s.ask.total}`
      : UI.subtitle;

  // Com launcherOnlyAfterStart, a isca só volta para retomar uma conversa
  // começada; aí ela já entra visível, sem a animação de chegada.
  const showLauncher = !launcherOnlyAfterStart || (started && !s.done);

  const closeChat = () => {
    onCloseOverlay?.();
    setOpen(false);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (s.busy || !value) return;
    const v = value;
    setValue('');
    flow.submit(v);
  };
  const onChange = (raw: string) =>
    setValue(field && field.mask === 'phone' ? maskPhoneBR(raw) : raw);

  const style = {
    '--asw-primary': tokens.primary,
    '--asw-primary-dark': tokens.primaryDark,
    '--asw-on-primary': tokens.onPrimary,
    '--asw-primary-text': tokens.primaryText,
    '--asw-x': `${offsetX}px`,
    '--asw-y': `${offsetY}px`,
  } as CSSProperties;

  return createPortal(
    <div className="asw-root" ref={rootRef} style={style}>
      {!open && showLauncher && tease && (
        <div className="asw-tease" role="status">
          {UI.teaser}
        </div>
      )}

      {!open && showLauncher && (
        <button
          type="button"
          className="asw-fab"
          data-state={launcherOnlyAfterStart ? 'in' : fabState}
          data-attn={attn ? '' : undefined}
          aria-haspopup="dialog"
          onClick={openChat}
        >
          <span className="asw-shine" aria-hidden="true" />
          <Spark />
          <span className="asw-fab-label">
            {started && !s.done ? UI.ctaContinue : UI.cta}
          </span>
        </button>
      )}

      {open && (
        <section
          className="asw-chat"
          role="dialog"
          aria-label={`${UI.title} ${brandName}`.trim()}
        >
          <div className="asw-head">
            <div className="asw-avatar">
              <Spark />
            </div>
            <div className="asw-titles">
              <b>
                {UI.title} {brandName}
              </b>
              <span>{subtitle}</span>
            </div>
            <button
              type="button"
              className="asw-close"
              aria-label={UI.close}
              onClick={closeChat}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M3 3l10 10M13 3L3 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </button>
          </div>
          <div className="asw-progress" aria-hidden="true">
            <i style={{ width: `${progress}%` }} />
          </div>

          {overlay && (
            <div className="asw-doc">
              <button
                type="button"
                className="asw-doc-back"
                onClick={onCloseOverlay}
              >
                ← {UI.back}
              </button>
              <div className="asw-doc-body">{overlay}</div>
            </div>
          )}

          <div
            className="asw-msgs"
            ref={msgsRef}
            role="log"
            aria-live="polite"
            hidden={!!overlay}
          >
            {s.msgs.map((m) =>
              m.who === 'bot' ? (
                <div key={m.id} className="asw-bubble asw-bot">
                  <Parts
                    parts={m.parts}
                    privacyUrl={privacyUrl}
                    termsUrl={termsUrl}
                    onOpenDoc={onOpenDoc}
                  />
                </div>
              ) : (
                <div key={m.id} className="asw-bubble asw-me">
                  {m.text}
                </div>
              )
            )}
            {s.typing && (
              <div className="asw-bubble asw-bot asw-typing">
                <span />
                <span />
                <span />
              </div>
            )}
            {s.consent && (
              <div className="asw-consent">
                <label className="asw-check">
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                  />
                  <span>{UI.consentCheck}</span>
                </label>
                <button
                  type="button"
                  className="asw-cta"
                  disabled={!agree}
                  onClick={() => flow.acceptConsent()}
                >
                  {UI.consentButton}
                </button>
              </div>
            )}
          </div>

          {!overlay && (
            <div className="asw-chips">
              {s.ask?.chips?.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="asw-chip"
                  onClick={() => {
                    if (!s.busy) flow.chip(c.id);
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {field && !s.done && !overlay && (
            <>
              <p className="asw-hint">{field.label}</p>
              <form className="asw-form" onSubmit={submit} autoComplete="off">
                <div className="asw-field">
                  <input
                    ref={inputRef}
                    type={field.secret && showPwd ? 'text' : field.type}
                    inputMode={field.inputMode}
                    autoComplete={field.autoComplete}
                    placeholder={field.placeholder}
                    aria-label="Sua resposta"
                    value={value}
                    disabled={s.busy}
                    onChange={(e) => onChange(e.target.value)}
                  />
                  {field.secret && (
                    <button
                      type="button"
                      className="asw-eye"
                      aria-label={UI.togglePassword}
                      onClick={() => setShowPwd((v) => !v)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className="asw-send"
                  aria-label={UI.send}
                  disabled={s.busy}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M4 12h15M13 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </button>
              </form>
            </>
          )}
        </section>
      )}
    </div>,
    document.body
  );
}
